import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useClickOutside from '@hooks/useClickOutside';

import { filterFieldType, isDetailsPage, toggleCheckbox } from '@features/filter/static/filterUtil';
import ComplianceGrade from '@features/supplier/component/ComplianceGrade';

import {
	removeItemFromGlobalFilter,
	storeItemInGlobalFilter,
	storeSelectedCategory,
	storeSelectedFilterCheckboxType,
	updateMultiLevelFilter
} from '@store/actions/FilterAction';

import { capitalizeFirstLetter } from '@utils/Util';

const Checkbox = ({
	isPrimary = true,
	data,
	handleSearch,
	type,
	nextFilterLevel = 3,
	onSelectedOption,
	className = 'filter-submenu scroll-y-label',
	isFocus = true,
	showSearchBar = true,
	isReplaceHyphens = true
}) => {
	const getFilterData = (store) =>
		isDetailsPage() ? store.filterFieldStore?.DETAILS_PAGE_FILTERS : store.filterFieldStore;
	const filterData = useSelector((store) => getFilterData(store));
	const selectedField = useSelector((store) => store.slectedCheckBoxFieldStore);
	const multiLevelFilterStore = useSelector((store) => store.multiLevelFIlterStore);
	const currentFilterLevel = multiLevelFilterStore?.currentSelectedFilterLevel;

	const isShowItemsCount = useSelector((store) => store.filters?.showItemsCount?.isShowItemsCount);
	const INFO = ['CERTIFICATE_STRENGTH', 'GRADE'];
	const dispatch = useDispatch();
	const checkboxRef = useRef(null);

	const onOutSideClick = () => {
		if (nextFilterLevel - 1 === 2 && currentFilterLevel <= 2) {
			dispatch(storeSelectedFilterCheckboxType(null));
			dispatch(storeSelectedCategory(null));
		}
	};
	useClickOutside(checkboxRef, onOutSideClick);

	const handleCheckbox = (e, title, categoryId, item) => {
		let { value, checked } = e.target;
		let currentStoreOfSelectedData = filterData[filterFieldType[type]['idName']] || [
			{
				name: filterFieldType[type]['idName'],
				key: value,
				value: title,
				identifier: `${filterFieldType[type]['idName']}_${value}`,
				checked: checked,
				categoryId: categoryId,
				parentCategoryName: item?.constant
			}
		];

		const dispatchFilterItem = (item) => {
			dispatch(
				storeItemInGlobalFilter({
					name: type === 'TEAM' ? item?.constant : filterFieldType[type]['idName'],
					key: item.id,
					value:
						type === 'TEAM' ? `${replaceHyphens(item?.constant, true)} ${item?.name}` : item.name,
					identifier: `${filterFieldType[type]['idName']}_${item.id}`,
					checked: checked,
					parentCategoryName: item?.constant,
					categoryId: categoryId
				})
			);
		};

		// when ALL clicked
		if (title === 'All') {
			if (currentStoreOfSelectedData) {
				data.forEach((item) => {
					if (
						!currentStoreOfSelectedData.some(
							(fields) => fields?.key?.toString() === item?.key?.toString()
						)
					) {
						dispatchFilterItem(item);
					}
				});
			} else {
				// at first All click to true
				data.forEach((item) => dispatchFilterItem(item));
			}
		} else {
			dispatchFilterItem({ id: value, name: title, constant: item?.constant });

			const lengthOfcurrentStoreOfSelectedData = currentStoreOfSelectedData?.filter(
				(item) => parseInt(item?.categoryId) === parseInt(categoryId)
			)?.length;

			// if every option selected than "All" will be auto selected.
			if (lengthOfcurrentStoreOfSelectedData === data.length - 1) {
				let allItemData = data?.find((item) => item?.name === 'All') || null;
				if (allItemData) {
					dispatchFilterItem({
						id: allItemData?.id,
						name: allItemData?.name
					});
				}
			}

			// if unselect any one option than "All" will be unselected.
			if (lengthOfcurrentStoreOfSelectedData === data.length) {
				let allItemData = data?.find((item) => item?.name === 'All') || null;
				if (allItemData) {
					dispatchFilterItem({
						id: allItemData?.id,
						name: allItemData?.name
					});
					dispatch(
						removeItemFromGlobalFilter({
							name: allItemData?.name,
							identifier: `${filterFieldType[type]['idName']}_${allItemData.id}`
						})
					);
				}
			}
		}
	};

	const onCloseMenu = (num) => {
		if (multiLevelFilterStore?.currentSelectedFilterLevel !== num) {
			dispatch(updateMultiLevelFilter(num));
		}
	};
	const replaceHyphens = (str) => {
		return capitalizeFirstLetter(str)?.split('_').join('-');
	};

	const handleCheckBoxSelection = (item) => {
		if (item.id !== 'ALL') {
			return toggleCheckbox(
				filterData,
				type === 'TEAM' ? item?.constant : filterFieldType[type]['idName'],
				`${filterFieldType[type]['idName']}_${item.id}`,
				item.id
			);
		} else {
			let optionKeys = data?.map((item) => item.id.toString()) || [];
			let selectedKeys =
				filterData[filterFieldType[type]['idName']]?.map((item) => item.key.toString()) || [];
			if (selectedKeys.length > 0) {
				return optionKeys.filter((item) => item !== 'ALL').every((id) => selectedKeys.includes(id));
			}
			return false;
		}
	};

	return (
		(selectedField?.selectedCheckboxField === type || !isPrimary) && (
			<div ref={checkboxRef} onClick={() => onCloseMenu(nextFilterLevel - 1)}>
				<div className={className}>
					<div className="card border-0">
						<div className="card-body">
							{showSearchBar && (
								<div className="form-group size36 m-0 search-filter mb-3">
									<div className="with-icon right-icon">
										<input
											type="text"
											placeholder="Search"
											onChange={handleSearch}
											autoFocus={isFocus}
											onClick={(e) => e.stopPropagation()}
											autoComplete="off"
										/>
										<span className="right-icon">
											<img src={`${process.env.PUBLIC_URL}/icons/search.png`} alt="" />
										</span>
									</div>
								</div>
							)}
							{INFO.includes(type) && (
								<div className="form-group d-flex align-items-center">
									<div className="mr-2">
										<span className="regular-14 rank-title text-capitalize">
											{type === INFO[1] ? 'Supplier rank' : 'Compliance Grade'}
										</span>
									</div>
									<div>
										<ComplianceGrade type={type} />
									</div>
								</div>
							)}
							{data?.map((item) => (
								<React.Fragment key={item?.id}>
									{!item.subCategory && !item.hasInputField ? (
										<div className="custom-chekbox">
											<div className="form-group certificate-group">
												<input
													type="checkbox"
													id={`${filterFieldType[type]['idName']}_${item.id}`}
													name={`${filterFieldType[type]['idName']}_${item.id}`}
													value={item.id}
													onChange={(e) =>
														handleCheckbox(e, item.name, item?.categoryId, item)
													}
													checked={handleCheckBoxSelection(item)}
												/>
												<label
													className="regular-14 d-flex"
													htmlFor={`${filterFieldType[type]['idName']}_${item.id}`}
												>
													{item?.additionalImage && (
														<img
															src={item?.additionalImage}
															alt=""
															className="mx-1 pattern-image"
														/>
													)}
													{item?.hexCode && (
														<span
															className="color-icon mr-1"
															style={{
																background: item.hexCode
															}}
														/>
													)}

													<span>
														{capitalizeFirstLetter(item?.name)}{' '}
														{isShowItemsCount && item.count && `(${item?.count})`}
													</span>
												</label>
											</div>
										</div>
									) : (
										<li
											className={`regular-14 gray_dark_01 d-flex justify-content-between ${
												multiLevelFilterStore?.multiLevelFilters?.find(
													(item) => item?.filterLevel === nextFilterLevel
												)?.id === item.id
													? 'selected'
													: ''
											}`}
											onClick={(e) => {
												e.stopPropagation();
												dispatch(
													storeSelectedCategory({
														...item,
														filterLevel: nextFilterLevel
													})
												);
												onSelectedOption && onSelectedOption(item);
											}}
											key={item?.id}
										>
											<span>
												{isReplaceHyphens ? replaceHyphens(item?.name) : item?.name}{' '}
												{item.count && `(${item?.count})`}
											</span>

											<span>
												<img
													src={`${process.env.PUBLIC_URL}/icons/right-arrow.svg`}
													alt="right"
												/>
											</span>
										</li>
									)}
								</React.Fragment>
							))}
						</div>
					</div>
				</div>
			</div>
		)
	);
};

export default Checkbox;
